import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { categories as catApi, items as itemApi, locations as locApi, tags as tagApi } from '@/api/endpoints';
import type { ItemCondition, ItemView, LocationNode } from '@/types/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LocationQuickCreateDialog } from '@/components/LocationQuickCreateDialog';
import { ItemPhotoSection } from '@/components/ItemPhotoSection';
import { PhotoLightbox } from '@/components/PhotoLightbox';

export const CONDITIONS: ItemCondition[] = ['NEW', 'LIKE_NEW', 'GOOD', 'FAIR', 'POOR', 'BROKEN'];

export function ItemFormPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [zoomed, setZoomed] = useState<string | null>(null);
  // Picked before the item exists yet (create mode only, issue #131) -- there's
  // nothing to upload them to until `submit` below creates the row, so they ride
  // along with that call instead, mirroring the mobile app's own pendingPhotos.
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [pendingReceipts, setPendingReceipts] = useState<File[]>([]);
  // ItemFormPage only ever renders behind RequireAuth, so user is always set here.
  const { user } = useAuth();

  const cats = useQuery({ queryKey: ['categories'], queryFn: catApi.list });
  const tagsQuery = useQuery({ queryKey: ['tags'], queryFn: tagApi.list });
  const locTree = useQuery({ queryKey: ['locations', 'tree'], queryFn: locApi.tree });
  const existing = useQuery({
    queryKey: ['items', id],
    queryFn: () => itemApi.get(Number(id)),
    enabled: isEdit,
  });

  const [form, setForm] = useState<Partial<ItemView> & { tagIds?: number[]; quantity: number }>(() => {
    if (isEdit) return { quantity: 1 };
    // Arriving from a location-filtered items page preselects that location.
    const paramLocationId = searchParams.get('locationId');
    const locationId = paramLocationId ? Number(paramLocationId) : undefined;
    return {
      quantity: 1,
      // New items start from the user's saved defaults; they can still freely
      // edit either field before saving, same as any other.
      currency: user!.defaultCurrency,
      condition: user!.defaultCondition,
      ...(locationId !== undefined && !Number.isNaN(locationId) ? { locationId } : {}),
    };
  });

  useEffect(() => {
    if (existing.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seeds the editable form from the fetched item
      setForm({ ...existing.data, tagIds: existing.data.tags.map((tg) => tg.id) });
    }
  }, [existing.data]);

  const flatLocations = (() => {
    const out: { id: number; label: string }[] = [];
    const walk = (nodes: LocationNode[], prefix: string) => {
      for (const n of nodes) {
        out.push({ id: n.id, label: prefix + n.name });
        walk(n.children, prefix + n.name + ' / ');
      }
    };
    walk(locTree.data ?? [], '');
    return out;
  })();
  // Guards against the flash of the empty-state banner while the query is still
  // pending (flatLocations is [] at that point too, from the ?? [] fallback above).
  const noLocationsAvailable = locTree.isSuccess && flatLocations.length === 0;

  // Edit mode already has an id, so a picked photo/receipt uploads immediately
  // (same as ItemDetailPage) instead of joining the pending queue below.
  const uploadPhoto = useMutation({
    mutationFn: (file: File) => itemApi.uploadPhoto(Number(id), file, 'ITEM'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });
  const uploadReceipt = useMutation({
    mutationFn: (file: File) => itemApi.uploadPhoto(Number(id), file, 'RECEIPT'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });
  const removePhoto = useMutation({
    mutationFn: (photoId: number) => itemApi.removePhoto(Number(id), photoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });

  const onPickPhoto = (file: File) => {
    if (isEdit) uploadPhoto.mutate(file);
    else setPendingPhotos((files) => [...files, file]);
  };
  const onPickReceipt = (file: File) => {
    if (isEdit) uploadReceipt.mutate(file);
    else setPendingReceipts((files) => [...files, file]);
  };

  const submit = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        description: form.description,
        locationId: form.locationId,
        categoryId: form.categoryId,
        quantity: form.quantity,
        brand: form.brand,
        model: form.model,
        purchaseDate: form.purchaseDate,
        purchasePrice: form.purchasePrice,
        currency: form.currency,
        currentValue: form.currentValue,
        warrantyUntil: form.warrantyUntil,
        condition: form.condition,
        tagIds: form.tagIds ?? [],
      };
      const saved = isEdit
        ? await itemApi.update(Number(id), payload)
        : await itemApi.create(payload);
      // Only ever non-empty in create mode: an edit's photos/receipts already
      // uploaded immediately via onPickPhoto/onPickReceipt above.
      for (const file of pendingPhotos) {
        await itemApi.uploadPhoto(saved.id, file, 'ITEM');
      }
      for (const file of pendingReceipts) {
        await itemApi.uploadPhoto(saved.id, file, 'RECEIPT');
      }
      return saved;
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
      navigate(`/items/${saved.id}`);
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate();
  };

  return (
    <>
      <Card className="max-w-3xl">
        <CardHeader><CardTitle>{isEdit ? t('items.edit') : t('items.create')}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            {noLocationsAvailable ? (
              <div className="space-y-2 rounded-md border border-dashed p-4 text-sm">
                <p className="font-medium">{t('items.locationRequiredBannerTitle')}</p>
                <p className="text-muted-foreground">{t('items.locationRequiredBannerBody')}</p>
                <Button type="button" variant="secondary" size="sm" onClick={() => setQuickCreateOpen(true)}>
                  {t('items.locationRequiredCta')}
                </Button>
              </div>
            ) : (
              <>
                <Label htmlFor="loc">{t('items.location')}</Label>
                <select id="loc" required
                        value={form.locationId ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, locationId: Number(e.target.value) }))}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="" disabled>—</option>
                  {flatLocations.map((l) => (<option key={l.id} value={l.id}>{l.label}</option>))}
                </select>
              </>
            )}
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="name">{t('items.name')}</Label>
            <Input id="name" value={form.name ?? ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cat">{t('items.category')}</Label>
            <select id="cat"
                    value={form.categoryId ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value ? Number(e.target.value) : undefined }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">—</option>
              {(cats.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{i18n.language?.startsWith('tr') ? c.nameTr : c.nameEn}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="qty">{t('items.quantity')}</Label>
            <Input id="qty" type="number" min={1} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="brand">{t('items.brand')}</Label>
            <Input id="brand" value={form.brand ?? ''} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="model">{t('items.model')}</Label>
            <Input id="model" value={form.model ?? ''} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pdate">{t('items.purchaseDate')}</Label>
            <Input id="pdate" type="date" value={form.purchaseDate ?? ''} onChange={(e) => setForm((f) => ({ ...f, purchaseDate: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="price">{t('items.purchasePrice')}</Label>
            <Input id="price" type="number" step="0.01" value={form.purchasePrice ?? ''} onChange={(e) => setForm((f) => ({ ...f, purchasePrice: e.target.value ? Number(e.target.value) : null }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ccy">{t('items.currency')}</Label>
            <Input id="ccy" placeholder="TRY" maxLength={3} value={form.currency ?? ''} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="value">{t('items.currentValue')}</Label>
            <Input id="value" type="number" step="0.01" value={form.currentValue ?? ''} onChange={(e) => setForm((f) => ({ ...f, currentValue: e.target.value ? Number(e.target.value) : null }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="war">{t('items.warrantyUntil')}</Label>
            <Input id="war" type="date" value={form.warrantyUntil ?? ''} onChange={(e) => setForm((f) => ({ ...f, warrantyUntil: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cond">{t('items.condition')}</Label>
            <select id="cond"
                    value={form.condition ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, condition: (e.target.value || null) as ItemCondition | null }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">—</option>
              {CONDITIONS.map((c) => <option key={c} value={c}>{t(`items.conditions.${c}`)}</option>)}
            </select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="tags">{t('items.tags')}</Label>
            <select id="tags" multiple
                    value={(form.tagIds ?? []).map(String)}
                    onChange={(e) => setForm((f) => ({ ...f, tagIds: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) }))}
                    className="min-h-[6rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {(tagsQuery.data ?? []).map((tg) => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
            </select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="desc">{t('items.description')}</Label>
            <textarea id="desc" rows={3} value={form.description ?? ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <ItemPhotoSection
              title={t('items.photos')}
              photos={(existing.data?.photos ?? []).filter((p) => p.photoType === 'ITEM')}
              pending={pendingPhotos}
              addLabel={t('items.addPhoto')}
              openLabel={t('items.openPhoto')}
              removeLabel={t('items.removePhoto')}
              onUpload={onPickPhoto}
              onRemove={(photoId) => removePhoto.mutate(photoId)}
              onRemovePending={(index) => setPendingPhotos((files) => files.filter((_, i) => i !== index))}
              onZoom={setZoomed}
            />
          </div>
          <div className="md:col-span-2">
            <ItemPhotoSection
              title={t('items.receipts')}
              photos={(existing.data?.photos ?? []).filter((p) => p.photoType === 'RECEIPT')}
              pending={pendingReceipts}
              addLabel={t('items.addReceipt')}
              openLabel={t('items.openReceipt')}
              removeLabel={t('items.removeReceipt')}
              onUpload={onPickReceipt}
              onRemove={(photoId) => removePhoto.mutate(photoId)}
              onRemovePending={(index) => setPendingReceipts((files) => files.filter((_, i) => i !== index))}
              onZoom={setZoomed}
            />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button type="submit" loading={submit.isPending} disabled={!form.name || !form.locationId}>{t('common.save')}</Button>
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
          </div>
          </form>
        </CardContent>
      </Card>
      <LocationQuickCreateDialog
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        onCreated={(loc) => setForm((f) => ({ ...f, locationId: loc.id }))}
      />
      {zoomed && <PhotoLightbox src={zoomed} onClose={() => setZoomed(null)} />}
    </>
  );
}
